'use client';

import { useRef, useEffect, useMemo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { PermutationList, PermutationListItem } from '@/lib/types';
import api from '@/lib/api';

interface TagMatch {
  text: string;
  listName: string;
  listId: number;
  start: number;
  end: number;
}

interface Suggestion {
  item: PermutationListItem;
  listName: string;
  listId: number;
}

interface TagHighlightTextareaProps {
  value: string;
  onChange: (value: string) => void;
  lists: PermutationList[];
  onTagClick: (listId: number, listName: string, start: number, end: number) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}

const TagHighlightTextarea = forwardRef<HTMLTextAreaElement, TagHighlightTextareaProps>(function TagHighlightTextarea({
  value,
  onChange,
  lists,
  onTagClick,
  placeholder,
  className = '',
  required = false,
}, ref) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Auto-suggest state
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [matchInfo, setMatchInfo] = useState<{ start: number; word: string } | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const isTypingRef = useRef(false);

  // Context menu state for "Add to list"
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; text: string } | null>(null);
  const [isAddingToList, setIsAddingToList] = useState(false);

  // Forward the textarea ref to parent
  useImperativeHandle(ref, () => textareaRef.current!, []);

  // Build a flat list of all items for auto-suggest search
  // Sorted by length (longest first) to prioritize longer matches
  const allItems = useMemo(() => {
    const items: Suggestion[] = [];
    for (const list of lists) {
      for (const item of list.items) {
        if (item.is_active && item.value.trim()) {
          items.push({
            item,
            listName: list.name,
            listId: list.id,
          });
        }
      }
    }
    // Sort by value length descending - longer matches first
    items.sort((a, b) => b.item.value.length - a.item.value.length);
    return items;
  }, [lists]);

  // Build lookup: item value -> { listId, listName }
  // Sort items by length (longest first) to match longer phrases first
  const itemLookup = useMemo(() => {
    const lookup: Map<string, { listId: number; listName: string }> = new Map();

    // Collect all items with their list info
    const sortedItems: { value: string; listId: number; listName: string }[] = [];

    for (const list of lists) {
      for (const item of list.items) {
        if (item.is_active && item.value.trim()) {
          sortedItems.push({
            value: item.value.toLowerCase(),
            listId: list.id,
            listName: list.name,
          });
        }
      }
    }

    // Sort by length descending (longer matches take priority)
    sortedItems.sort((a, b) => b.value.length - a.value.length);

    // Build lookup (first match wins due to sort order)
    for (const item of sortedItems) {
      if (!lookup.has(item.value)) {
        lookup.set(item.value, { listId: item.listId, listName: item.listName });
      }
    }

    return lookup;
  }, [lists]);

  // Find all tag matches in the text
  const tags = useMemo((): TagMatch[] => {
    if (!value || itemLookup.size === 0) return [];

    const matches: TagMatch[] = [];
    const valueLower = value.toLowerCase();
    const usedRanges: { start: number; end: number }[] = [];

    // Check each item value against the text
    // Items are already sorted by length (longest first)
    for (const [itemValue, info] of itemLookup) {
      let searchStart = 0;

      while (true) {
        const index = valueLower.indexOf(itemValue, searchStart);
        if (index === -1) break;

        const start = index;
        const end = index + itemValue.length;

        // Check if this range overlaps with existing matches
        const overlaps = usedRanges.some(
          (range) => start < range.end && end > range.start
        );

        if (!overlaps) {
          matches.push({
            text: value.slice(start, end),
            listName: info.listName,
            listId: info.listId,
            start,
            end,
          });
          usedRanges.push({ start, end });
        }

        searchStart = index + 1;
      }
    }

    // Sort by position for rendering
    matches.sort((a, b) => a.start - b.start);

    return matches;
  }, [value, itemLookup]);

  // Auto-resize textarea to fit content (no internal scrollbar)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height to scrollHeight to fit all content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  // Get the word being typed at cursor position
  const getWordAtCursor = useCallback((text: string, cursorPos: number): { word: string; start: number } | null => {
    if (cursorPos === 0) return null;

    // Find the start of the current word (go back until whitespace or start)
    let start = cursorPos;
    while (start > 0 && !/\s/.test(text[start - 1])) {
      start--;
    }

    const word = text.slice(start, cursorPos);
    if (word.length < 2) return null;

    return { word, start };
  }, []);

  // Search for matching items
  const searchItems = useCallback((word: string): Suggestion[] => {
    const wordLower = word.toLowerCase();
    const matches: Suggestion[] = [];

    for (const suggestion of allItems) {
      if (suggestion.item.value.toLowerCase().startsWith(wordLower)) {
        matches.push(suggestion);
        if (matches.length >= 5) break;
      }
    }

    return matches;
  }, [allItems]);

  // Calculate cursor position in pixels using mirror div
  const calculateCursorPosition = useCallback((cursorPos: number) => {
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    const wrapper = wrapperRef.current;
    if (!textarea || !mirror || !wrapper) return null;

    // Copy text up to cursor into mirror
    const textBeforeCursor = value.slice(0, cursorPos);

    // Add a marker span at cursor position
    mirror.innerHTML = '';
    const textNode = document.createTextNode(textBeforeCursor);
    const marker = document.createElement('span');
    marker.textContent = '|';
    mirror.appendChild(textNode);
    mirror.appendChild(marker);

    // Get marker position relative to mirror
    const markerRect = marker.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();

    // Account for scroll position
    const scrollTop = wrapper.scrollTop;
    const scrollLeft = wrapper.scrollLeft;

    return {
      top: markerRect.top - wrapperRect.top + scrollTop + 24, // 24px line height below cursor
      left: markerRect.left - wrapperRect.left + scrollLeft,
    };
  }, [value]);

  // Close suggestions
  const closeSuggestions = useCallback(() => {
    setSuggestions([]);
    setSelectedIndex(0);
    setMatchInfo(null);
    setDropdownPosition(null);
  }, []);

  // Evaluate for suggestions (called on text change while typing)
  const evaluateSuggestions = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const wordInfo = getWordAtCursor(value, cursorPos);

    if (!wordInfo) {
      closeSuggestions();
      return;
    }

    const matches = searchItems(wordInfo.word);
    if (matches.length === 0) {
      closeSuggestions();
      return;
    }

    // Calculate position and show suggestions
    const position = calculateCursorPosition(cursorPos);
    if (position) {
      setSuggestions(matches);
      setSelectedIndex(0);
      setMatchInfo({ start: wordInfo.start, word: wordInfo.word });
      setDropdownPosition(position);
    }
  }, [value, getWordAtCursor, searchItems, calculateCursorPosition, closeSuggestions]);

  // Insert selected suggestion
  const insertSuggestion = useCallback((suggestion: Suggestion) => {
    if (!matchInfo) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const newValue = value.slice(0, matchInfo.start) + suggestion.item.value + value.slice(cursorPos);
    const newCursorPos = matchInfo.start + suggestion.item.value.length;

    onChange(newValue);
    closeSuggestions();

    // Set cursor position after React updates the value
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.selectionStart = newCursorPos;
        textareaRef.current.selectionEnd = newCursorPos;
        textareaRef.current.focus();
      }
    });
  }, [matchInfo, value, onChange, closeSuggestions]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // If suggestions are open, handle navigation/selection
    if (suggestions.length > 0) {
      switch (e.key) {
        case 'Escape':
        case 'ArrowLeft':
        case 'ArrowRight':
          e.preventDefault();
          closeSuggestions();
          return;

        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % suggestions.length);
          return;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
          return;

        case 'Tab':
        case 'Enter':
          e.preventDefault();
          insertSuggestion(suggestions[selectedIndex]);
          return;
      }
    }

    // Mark that we're typing (for distinguishing from mouse clicks)
    isTypingRef.current = true;
  }, [suggestions, selectedIndex, closeSuggestions, insertSuggestion]);

  // Handle text change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Close suggestions first, then re-evaluate after state update
    closeSuggestions();
  }, [onChange, closeSuggestions]);

  // Evaluate suggestions after value changes (only if typing)
  useEffect(() => {
    if (isTypingRef.current) {
      // Small delay to ensure cursor position is updated
      const timer = setTimeout(() => {
        evaluateSuggestions();
        isTypingRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [value, evaluateSuggestions]);

  // Handle mouse down - close suggestions when clicking in textarea
  const handleMouseDown = useCallback(() => {
    closeSuggestions();
    setContextMenu(null);
  }, [closeSuggestions]);

  // Handle context menu (right-click) - show "Add to list" option if text is selected
  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const selectedText = textarea.value.substring(textarea.selectionStart, textarea.selectionEnd).trim();
    if (selectedText) {
      e.preventDefault();
      const wrapper = wrapperRef.current;
      if (wrapper) {
        const rect = wrapper.getBoundingClientRect();
        setContextMenu({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          text: selectedText,
        });
      }
    }
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Add selected text to a list
  const handleAddToList = useCallback(async (listId: number) => {
    if (!contextMenu?.text) return;

    setIsAddingToList(true);
    try {
      await api.addListItem(listId, { value: contextMenu.text });
      setContextMenu(null);
    } catch (err) {
      console.error('Failed to add item to list:', err);
    } finally {
      setIsAddingToList(false);
    }
  }, [contextMenu]);

  // Color palette for different permutation lists (50% transparent)
  const listColors = [
    'rgba(59, 130, 246, 0.5)',   // blue
    'rgba(16, 185, 129, 0.5)',   // emerald
    'rgba(249, 115, 22, 0.5)',   // orange
    'rgba(168, 85, 247, 0.5)',   // purple
    'rgba(236, 72, 153, 0.5)',   // pink
    'rgba(234, 179, 8, 0.5)',    // yellow
    'rgba(20, 184, 166, 0.5)',   // teal
    'rgba(239, 68, 68, 0.5)',    // red
  ];

  // Get consistent color for a list based on its ID
  const getListColor = (listId: number) => {
    return listColors[listId % listColors.length];
  };

  // Render overlay content with highlighted tags
  const renderOverlayContent = () => {
    if (tags.length === 0) {
      return <span className="whitespace-pre-wrap break-words text-[var(--text-primary)]">{value || ' '}</span>;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    tags.forEach((tag, idx) => {
      // Add text before this tag (normal text color)
      if (tag.start > lastIndex) {
        parts.push(
          <span key={`text-${idx}`} className="text-[var(--text-primary)]">
            {value.slice(lastIndex, tag.start)}
          </span>
        );
      }

      // Add the highlighted tag with list-specific color
      const bgColor = getListColor(tag.listId);
      parts.push(
        <span
          key={`tag-${idx}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTagClick(tag.listId, tag.listName, tag.start, tag.end);
          }}
          className="
            rounded px-0.5 -mx-0.5
            text-[var(--text-primary)] cursor-pointer
            hover:brightness-125 pointer-events-auto
            transition-all duration-150
          "
          style={{ backgroundColor: bgColor }}
          title={`From list "${tag.listName}" - click to change`}
        >
          {tag.text}
        </span>
      );

      lastIndex = tag.end;
    });

    // Add remaining text (normal text color)
    if (lastIndex < value.length) {
      parts.push(
        <span key="text-end" className="text-[var(--text-primary)]">
          {value.slice(lastIndex)}
        </span>
      );
    }

    return <>{parts}</>;
  };

  // Shared styles for pixel-perfect alignment between textarea and overlay
  // Both layers use identical styles, no scrollbars - wrapper handles scrolling
  const sharedStyles: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: '15px',
    lineHeight: '24px',
    fontFamily: 'inherit',
    letterSpacing: 'normal',
    wordSpacing: '0px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    overflow: 'hidden', // No scrollbars on either layer
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    // Scrollable wrapper - this handles all scrolling
    // className from parent provides sizing (min-h, max-h) and input styling
    <div
      ref={wrapperRef}
      className={`relative overflow-auto focus-within:border-[var(--accent)] focus-within:shadow-[0_0_0_3px_var(--accent-soft)] ${className}`}
    >
      {/* Inner container that grows with content */}
      <div className="relative min-h-full">
        {/* Actual textarea - receives keyboard input, grows with content */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          onContextMenu={handleContextMenu}
          className="relative bg-transparent caret-[var(--text-primary)] resize-none w-full"
          placeholder={placeholder}
          required={required}
          style={{
            ...sharedStyles,
            color: 'transparent',
            caretColor: 'var(--text-primary)',
            background: 'transparent',
            border: 'none',
            outline: 'none',
          }}
        />

        {/* Highlight overlay - same size as textarea, no scrolling */}
        <div
          className="absolute inset-0 pointer-events-none z-20"
          style={{
            ...sharedStyles,
            background: 'transparent',
          }}
          aria-hidden="true"
        >
          {renderOverlayContent()}
        </div>

        {/* Mirror div for cursor position calculation - hidden */}
        <div
          ref={mirrorRef}
          aria-hidden="true"
          style={{
            ...sharedStyles,
            position: 'absolute',
            top: 0,
            left: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Auto-suggest dropdown */}
      {suggestions.length > 0 && dropdownPosition && (
        <div
          className="absolute z-50 bg-[var(--bg-elevated)] border border-[var(--border-default)] rounded-lg shadow-lg overflow-hidden"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: '280px',
            maxWidth: '400px',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.listId}-${suggestion.item.id}`}
              onClick={() => insertSuggestion(suggestion)}
              className={`
                flex items-center gap-3 px-3 py-2 cursor-pointer
                ${index === selectedIndex ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--bg-surface)]'}
              `}
            >
              {/* Thumbnail */}
              <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-[var(--bg-surface)]">
                {suggestion.item.preview_image ? (
                  <img
                    src={api.getImageUrl(suggestion.item.preview_image)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)]">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Text content */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {suggestion.item.value}
                </div>
                <div className="text-xs text-[var(--text-muted)] truncate">
                  {suggestion.listName}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Context menu for "Add to list" */}
      {contextMenu && (
        <div
          className="absolute z-50 bg-[var(--bg-elevated)] border border-[var(--border-default)] rounded-lg shadow-lg overflow-hidden min-w-[200px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 text-xs text-[var(--text-muted)] border-b border-[var(--border-default)] truncate max-w-[300px]">
            Add &ldquo;{contextMenu.text}&rdquo; to:
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {lists.map((list) => (
              <button
                key={list.id}
                onClick={() => handleAddToList(list.id)}
                disabled={isAddingToList}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-surface)] disabled:opacity-50 flex items-center gap-2"
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: listColors[list.id % listColors.length] }}
                />
                <span className="truncate">{list.name}</span>
                <span className="text-xs text-[var(--text-muted)] ml-auto">{list.items.length}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default TagHighlightTextarea;
export type TagHighlightTextareaRef = HTMLTextAreaElement;
